import {
  useVillageBulkDelete,
  useVillages,
  useExportVillage,
} from "@/api/village";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDelete,
  swalError,
  swalErrorDefault,
  swalSuccess,
} from "@/utils/lib/swal";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DownloadButton } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge.tsx";
import { axiosInstance } from "@/utils/lib/axios";
import { getBadgeVariant } from "@/utils/helper/getBadgeVariant";
import { getExceedDuration } from "@/utils/helper/getExceedDuration";
import { useProfileByKeycloakId } from "@/api/auth";
import { nullElement } from "@/components/null-element";

const customSpecAdmin = generateCustomSpec({
  path: "",
  canBulk: true,
  fields: [
    {
      label: "Sub District Name",
      name: "villageName",
      sortable: true,
    },
    {
      label: "District Name",
      name: "subdistrict.subdistrictName",
      sortable: true,
    },
    {
      label: "City Name",
      name: "subdistrict.city.cityName",
      sortable: true,
    },
    {
      label: "Province Name",
      name: "subdistrict.city.province.provinceName",
      sortable: true,
    },
    {
      label: "Postal Code",
      name: "postalCode",
      sortable: true,
    },
    {
      label: "Sub District Code",
      name: "villageCode",
      sortable: true,
    },
  ],
});

const customSpecNonAdmin = generateCustomSpec({
  path: "",
  canBulk: false,
  fields: [
    {
      label: "Sub District Name",
      name: "villageName",
      sortable: true,
    },
    {
      label: "District Name",
      name: "subdistrict.subdistrictName",
      sortable: true,
    },
    {
      label: "City Name",
      name: "subdistrict.city.cityName",
      sortable: true,
    },
    {
      label: "Province Name",
      name: "subdistrict.city.province.provinceName",
      sortable: true,
    },
    {
      label: "Postal Code",
      name: "postalCode",
      sortable: true,
    },
    {
      label: "Sub District Code",
      name: "villageCode",
      sortable: true,
    },
  ],
});

const HeaderElement = ({
  dataConfig,
  initStatus,
}: {
  dataConfig: DataConfig;
  initStatus: string | null;
}) => {
  const [status, setStatus] = useState<string>("IDLE");
  const { mutate: exportList, isPending: isDownloading } = useExportVillage();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });

  const downloadTimeStamp = localStorage.getItem("isDownloadVillage") ?? "";
  const isDownloadSubDistrict = Boolean(
    downloadTimeStamp && downloadTimeStamp.trim().length > 0,
  );
  const stat =
    initStatus && Boolean(isDownloadSubDistrict) ? initStatus : status;

  const handleExport = () => {
    const params = {
      ...dataConfig,
      entityId: data?.id,
      limit: undefined,
      page: undefined,
    };

    if (stat === "IDLE") {
      setStatus("IN PROGRESS");
    }

    exportList(
      {
        config: params,
        isDownload: isDownloadSubDistrict,
        onDownload: !!isDownloadSubDistrict,
      },
      {
        onSuccess: (response) => {
          setStatus(response.status);
        },
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Badge variant={getBadgeVariant(stat)}>{stat}</Badge>&nbsp;
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function VillageTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false); // Add state to track processing
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();

  const { data: villages } = useVillages(dataConfig);
  const { mutate, isPending } = useVillageBulkDelete();

  const { roles } = useCurrentUser();
  const isVendor = roles?.includes(ROLE.VENDOR);
  const isValidator = roles?.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isSysAdminView = roles?.includes(ROLE.SYS_ADMIN_VIEW);
  const isVendorView = roles?.includes(ROLE.VENDOR_VIEW);

  const [status, setStatus] = useState<string | null>(null);
  const { mutate: exportList } = useExportVillage();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });

  useEffect(() => {
    const handleExport = async () => {
      const params = {
        ...dataConfig,
        entityId: data?.id,
        limit: undefined,
        page: undefined,
      };
      const downloadTimestamp = localStorage.getItem("isDownloadVillage");
      const isDownloadVillage = Boolean(
        downloadTimestamp && downloadTimestamp.trim().length > 0,
      );

      if (isDownloadVillage && !isProcessing) {
        setIsProcessing(true);
        try {
          // Fetch duration from API
          const response = await axiosInstance.get(
            "/api/crud/master-param?paramType=ITV_ASYNC_IDLE&active=true",
          );

          const exceedDuration = getExceedDuration(
            downloadTimestamp ?? "",
            response,
          );

          if (exceedDuration) {
            localStorage.removeItem("isDownloadVillage");
            setStatus(null);
            setIsProcessing(false);
          } else {
            exportList(
              {
                config: params,
                isDownload: isDownloadVillage,
                onDownload: false,
              },
              {
                onSuccess: (response) => {
                  setStatus(response.status);
                  setIsProcessing(false);
                },
                onError: () => {
                  swalErrorDefault.fire();
                  setIsProcessing(false);
                },
              },
            );
          }
        } catch (error) {
          console.error("Error fetching duration:", error);
          setIsProcessing(false);
        }
      }
    };

    handleExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportList, isProcessing]);

  function handleBulkDelete() {
    swalDelete.fire({
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "Village has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );

        return false;
      },
    });
  }

  const disabled = isVendor || isValidator || isSysAdminView || isVendorView;

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={disabled ? null : "/master-data/subdistrict/create"}
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={disabled ? null : handleBulkDelete}
        isPendingDelete={isPending}
      />
      <TableDefault
        spec={isVendor || isValidator ? customSpecNonAdmin : customSpecAdmin}
        data={villages}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        searchFilterByColumns={true}
        headerElement={
          <HeaderElement dataConfig={dataConfig} initStatus={status} />
        }
        customButtonEdit={
          isSysAdminView || isVendorView ? nullElement : undefined
        }
      />
    </>
  );
}
